import { AttestationRequest, IPv8API, VerificationOutputMap } from './IPv8API';
import { Dict } from "./types/Dict";
import { CallbackDict } from './util/CallbackDict';
import { interval, IntervalSubscription } from './util/interval';

const log = console.log

export class IPv8Service {
    public config = {
        pollIntervalInMillis: 500,
        minimumVerificationProbability: 0.85
    }
    public results: Dict<number> = {};

    private connectionCallbacks = new CallbackDict<(b: boolean) => void>()
    private verificationCallbacks = new CallbackDict<(b: any) => void>()
    private attestationRequestCallbacks = new CallbackDict<(a: AttestationRequest) => void>()
    private interval: IntervalSubscription

    constructor(private api: IPv8API) { }

    public start() {
        this.interval = interval(this.config.pollIntervalInMillis).subscribe(() => {
            this.pollAttestationRequests()
            this.pollVerificationOutputs()
            this.pollPeers()
        })
    }

    public stop() {
        if (this.interval) {
            this.interval.unsubscribe()
        }
    }

    /** Promises a boolean, true when the peer is found, or false when time runs out. */
    public async awaitConnection(mid_b64: string): Promise<any> {
        log(`IPv8_SERVICE: Peer ${mid_b64} is known.`)
        const peers = await this.api.listPeers()
        if (peers.indexOf(mid_b64) >= 0) {
            return true
        } else {
            log(`IPv8_SERVICE: Looking for peer ${mid_b64}..`)
            this.api.connectPeer(mid_b64)
            return new Promise(resolve => this.connectionCallbacks.register(mid_b64, resolve)).then(() =>
                log(`IPv8_SERVICE: Peer ${mid_b64} found!`)
            )
        }
    }

    /** Promises a boolean, true when the given attribute is verified, false if not. */
    public awaitVerification(
        mid_b64: string,
        attribute_hash_b64: string,
        attribute_value: string
    ): Promise<boolean> {
        return this.awaitConnection(mid_b64).then(() => {
            log(
                `IPv8_SERVICE: Requesting verification from ${mid_b64} on hash,value: ` +
                `${attribute_hash_b64}, ${attribute_value}.`
            )
            this.api.requestVerification(mid_b64, attribute_hash_b64, attribute_value)
            return new Promise<boolean>(resolve => {
                this.verificationCallbacks.register(attribute_hash_b64, resolve)
            }).then(answer => {
                log(`IPv8_SERVICE: Verification received from ${mid_b64}:`, answer)
                return answer
            })
        })
    }

    /** Promises an attestation request for a given mid */
    public awaitAttestationRequest(mid_b64: string): Promise<AttestationRequest> {
        return new Promise(resolve => this.attestationRequestCallbacks.register(mid_b64, resolve))
    }

    /** Attempts to make an attestation */
    public attest(
        mid_b64: string,
        attribute_name: string,
        attribute_value: string
    ): Promise<boolean> {
        return this.api.attest(mid_b64, attribute_name, attribute_value)
    }

    protected pollPeers() {
        return this.api.listPeers().then(results => this.handleNewPeers(results))
    }

    protected handleNewPeers(peers: string[]) {
        peers.forEach(peer => this.connectionCallbacks.call(peer)(true))
    }

    protected pollVerificationOutputs() {
        return this.api
            .listVerificationOutputs()
            .then(results => this.handleVerificationOutputs(results))
    }

    protected handleVerificationOutputs(results: VerificationOutputMap) {
        Object.entries(results).forEach(([attribute_hash, value]) => {
            const prob = value[0].probability;
            if (!(attribute_hash in this.results) || this.results[attribute_hash] !== prob) {
                console.log(">> VERIOUT: ", attribute_hash, prob);
            }
            this.results[attribute_hash] = prob;
            return value[0].probability > this.config.minimumVerificationProbability
                ? this.verificationCallbacks.call(attribute_hash)(true)
                : null
        }) // Note, should only call once!
    }

    protected pollAttestationRequests() {
        return this.api
            .listAttestationRequests()
            .then(requests => this.handleAttestationRequests(requests))
    }

    protected handleAttestationRequests(requests: AttestationRequest[]) {
        requests.forEach(result => {
            this.attestationRequestCallbacks.call('*')(result)
            this.attestationRequestCallbacks.call(result.mid_b64)(result)
        })
    }
}
